"use client"

import type React from "react"

import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import {
  Users,
  Mail,
  Phone,
  MapPin,
  CheckCircle,
  Heart,
  Sparkles,
  ArrowRight,
  MessageCircle,
  Handshake,
  XCircle,
  AlertCircle,
} from "lucide-react"
import axios from "axios"
import ImagesDev from "../api/images"

interface VolunteerFormData {
  full_name: string
  email: string
  phone: string
  city_country: string
  is_tso_member: string
  connection_duration: string
  why_volunteer: string
  department_interests: string[]
  has_experience: string
  experience_description: string
  skills_talents: string
  open_to_different_dept: string
  willing_attend_training: string
  willing_serve_humbly: string
  willing_communicate_changes: string
  additional_info: string
  isEmailAvailable: boolean | null
  isPhoneAvailable: boolean | null
}

const departments = [
  "Media team",
  "Community managers",
  "Prayer",
  "Mentorship with HS Administration",
  "Scribe",
  "Web design",
  "Follow up",
  "Welfare",
  "Christ in school",
  "BST subgroup",
]

async function checkEmailAvailability(email: string): Promise<boolean | null> {
  try {
    const res = await fetch("https://tsoministries.com/wp-json/custom/v1/get-volunteers/")
    if (!res.ok) throw new Error("Request failed")

    const data = await res.json()
    if (!Array.isArray(data)) return null

    const exists = data.some(
      (item) => item.email?.toLowerCase() === email.toLowerCase()
    )

    return !exists
  } catch (err) {
    console.error("Email check failed:", err)
    return null
  }
}

async function checkPhoneAvailability(phone: string): Promise<boolean | null> {
  try {
    const res = await fetch("https://tsoministries.com/wp-json/custom/v1/get-volunteers/")
    if (!res.ok) throw new Error("Request failed")

    const data = await res.json()
    if (!Array.isArray(data)) return null

    const exists = data.some(
      (item) => item.phone === phone
    )

    return !exists
  } catch (err) {
    console.error("Phone check failed:", err)
    return null
  }
}

const VolunteerEmailInput = ({ value, onChange, setFormData }: any) => {
  const [email, setEmail] = useState(value || "")
  const [isValid, setIsValid] = useState(true)
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null)
  const [showEmailWarning, setShowEmailWarning] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!email) {
      setIsValid(true)
      setIsAvailable(null)
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const isEmailValid = emailRegex.test(email)
    setIsValid(isEmailValid)
    setShowEmailWarning(true)

    if (!isEmailValid) {
      setIsAvailable(null)
      return
    }

    const delay = setTimeout(async () => {
      setLoading(true)
      try {
        const available = await checkEmailAvailability(email)
        setIsAvailable(available)
      } catch (error) {
        console.error("Error checking email availability:", error)
        setIsAvailable(null)
      } finally {
        setLoading(false)
      }
    }, 500)

    return () => clearTimeout(delay)
  }, [email])

  useEffect(() => {
    if (!email) {
      setFormData((prev: any) => ({ ...prev, isEmailAvailable: null }))
      return
    }

    if (isValid) {
      setFormData((prev: any) => ({ ...prev, isEmailAvailable: isAvailable }))
    } else {
      setFormData((prev: any) => ({ ...prev, isEmailAvailable: false }))
    }
  }, [email, isValid, isAvailable, setFormData])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    onChange(e)
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700">
        Email Address <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="email"
          name="email"
          value={email}
          onChange={handleChange}
          className={`w-full pl-11 pr-10 py-3 rounded-xl border-2 ${!isValid && email.length > 0 ? "border-red-400" : isAvailable === false ? "border-red-400" : "border-gray-200"} focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all`}
          placeholder="you@example.com"
        />
        {loading ? (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">Checking...</div>
        ) : (
          showEmailWarning &&
          isValid && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {isAvailable === true ? (
                <CheckCircle className="text-green-500 w-5 h-5" />
              ) : isAvailable === false ? (
                <XCircle className="text-red-500 w-5 h-5" />
              ) : (
                email.length >= 1 && <AlertCircle className="text-yellow-500 w-5 h-5" />
              )}
            </div>
          )
        )}
      </div>

      {showEmailWarning && !isValid && email.length > 0 && (
        <p className="text-red-500 text-sm">Invalid email format.</p>
      )}

      {showEmailWarning && isValid && isAvailable === false && (
        <p className="text-red-500 text-sm">This email is already registered. You have already volunteered.</p>
      )}

      {email.length >= 1 && showEmailWarning && isValid && isAvailable === null && !loading && (
        <p className="text-yellow-500 text-sm">Unable to verify email at the moment. Please try again shortly.</p>
      )}
    </div>
  )
}

const VolunteerPhoneInput = ({ value, onChange, setFormData }: any) => {
  const [phone, setPhone] = useState(value || "")
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null)
  const [showPhoneWarning, setShowPhoneWarning] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!phone || phone.length < 5) {
      setIsAvailable(null)
      return
    }

    setShowPhoneWarning(true)

    const delay = setTimeout(async () => {
      setLoading(true)
      try {
        const available = await checkPhoneAvailability(phone)
        setIsAvailable(available)
      } catch (error) {
        console.error("Error checking phone availability:", error)
        setIsAvailable(null)
      } finally {
        setLoading(false)
      }
    }, 500)

    return () => clearTimeout(delay)
  }, [phone])

  useEffect(() => {
    if (!phone) {
      setFormData((prev: any) => ({ ...prev, isPhoneAvailable: null }))
      return
    }

    setFormData((prev: any) => ({ ...prev, isPhoneAvailable: isAvailable }))
  }, [phone, isAvailable, setFormData])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(e.target.value)
    onChange(e)
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700">
        Phone Number (WhatsApp) <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="tel"
          name="phone"
          value={phone}
          onChange={handleChange}
          className={`w-full pl-11 pr-10 py-3 rounded-xl border-2 ${isAvailable === false ? "border-red-400" : "border-gray-200"} focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all`}
          placeholder="+1234567890"
        />
        {loading ? (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">Checking...</div>
        ) : (
          showPhoneWarning &&
          phone.length >= 5 && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {isAvailable === true ? (
                <CheckCircle className="text-green-500 w-5 h-5" />
              ) : isAvailable === false ? (
                <XCircle className="text-red-500 w-5 h-5" />
              ) : (
                <AlertCircle className="text-yellow-500 w-5 h-5" />
              )}
            </div>
          )
        )}
      </div>

      {showPhoneWarning && isAvailable === false && (
        <p className="text-red-500 text-sm">This phone number is already registered. You have already volunteered.</p>
      )}

      {phone.length >= 5 && showPhoneWarning && isAvailable === null && !loading && (
        <p className="text-yellow-500 text-sm">Unable to verify phone at the moment. Please try again shortly.</p>
      )}
    </div>
  )
}

export default function VolunteerForm() {
  const [formData, setFormData] = useState<VolunteerFormData>({
    full_name: "",
    email: "",
    phone: "",
    city_country: "",
    is_tso_member: "",
    connection_duration: "",
    why_volunteer: "",
    department_interests: [],
    has_experience: "",
    experience_description: "",
    skills_talents: "",
    open_to_different_dept: "",
    willing_attend_training: "",
    willing_serve_humbly: "",
    willing_communicate_changes: "",
    additional_info: "",
    isEmailAvailable: null,
    isPhoneAvailable: null,
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [bgImage, setBgImage] = useState("")

  useEffect(() => {
    setBgImage(getRandomHeroImage())
  }, [])

  const getRandomHeroImage = () => {
    const images = window.innerWidth > 768 ? ImagesDev.heroImagesDesk : ImagesDev.heroImages
    return images[Math.floor(Math.random() * images.length)]
  }

  const handleInputChange = (field: keyof VolunteerFormData, value: any) => {
    setFormData({ ...formData, [field]: value })
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" })
    }
  }

  const toggleDepartment = (dept: string) => {
    const current = formData.department_interests
    if (current.includes(dept)) {
      handleInputChange(
        "department_interests",
        current.filter((d) => d !== dept),
      )
    } else {
      handleInputChange("department_interests", [...current, dept])
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.full_name.trim()) newErrors.full_name = "Full name is required"
    if (!formData.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format"
    } else if (formData.isEmailAvailable === false) {
      newErrors.email = "This email is already registered"
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required"
    } else if (formData.isPhoneAvailable === false) {
      newErrors.phone = "This phone number is already registered"
    }
    if (!formData.city_country.trim()) newErrors.city_country = "City/Country is required"
    if (!formData.is_tso_member) newErrors.is_tso_member = "Please select an option"
    if (!formData.connection_duration) newErrors.connection_duration = "Please select an option"
    if (!formData.why_volunteer.trim()) newErrors.why_volunteer = "Please share why you want to volunteer"
    if (formData.department_interests.length === 0)
      newErrors.department_interests = "Please select at least one department"
    if (!formData.has_experience) newErrors.has_experience = "Please select an option"
    if (!formData.skills_talents.trim()) newErrors.skills_talents = "Please share your skills and talents"
    if (!formData.open_to_different_dept) newErrors.open_to_different_dept = "Please select an option"
    if (!formData.willing_attend_training) newErrors.willing_attend_training = "Please select an option"
    if (!formData.willing_serve_humbly) newErrors.willing_serve_humbly = "Please select an option"
    if (!formData.willing_communicate_changes) newErrors.willing_communicate_changes = "Please select an option"

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      const firstError = document.querySelector('[data-error="true"]')
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }

    setIsSubmitting(true)

    try {
      const dataToSubmit = {
        ...formData,
        department_interests: formData.department_interests.join(", "),
      }

      const res = await axios.post("https://tsoministries.com/wp-json/custom/v1/submit-volunteer/", dataToSubmit, {
        headers: { "Content-Type": "application/json" },
      })

      if (res.status === 200) {
        setSubmitSuccess(true)
        window.scrollTo({ top: 0, behavior: "smooth" })
      }
    } catch (err: any) {
      console.error(err)
      alert(err.response?.data?.error || "An error occurred. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full mx-auto mb-6 flex items-center justify-center"
          >
            <CheckCircle className="w-14 h-14 text-white" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl font-bold text-gray-800 mb-4"
          >
            Welcome to the Team! 🎉
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-gray-600 mb-8 text-lg"
          >
            Thank you for choosing to serve with TSO Ministries. Your commitment to making a difference is truly
            appreciated!
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-6 mb-8"
          >
            <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center justify-center gap-2">
              <Sparkles className="w-6 h-6 text-emerald-600" />
              Next Steps
            </h3>
            <p className="text-gray-700 mb-6">
              Join our volunteer community on WhatsApp to stay connected and receive important updates!
            </p>
            <a
              href="https://chat.whatsapp.com/Fz2bagzDLGQL4JlYmYDrWM"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-8 py-4 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              Join WhatsApp Group
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="border-t border-gray-200 pt-6"
          >
            <p className="text-gray-600 mb-4">Want to support us financially and become a partner?</p>
            <a
              href="/partner-form"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-600 text-white px-8 py-4 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              <Heart className="w-5 h-5" />
              Become a Partner
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-emerald-800 via-teal-800 to-cyan-800 py-12 px-4 sm:px-6 lg:px-8">
      <motion.img
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ duration: 2 }}
        className="absolute top-0 left-0 bottom-0 h-[100%] w-full"
        src={bgImage}
        style={{ objectFit: "cover" }}
      />
      <div className="relative max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="opacity-0 inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-lg mb-6">
            <Users className="w-6 h-6 text-emerald-600" />
            <span className="text-emerald-600 font-semibold">TSO Ministries</span>
          </div>
          <h1 className="text-5xl font-bold text-gray-100 mb-4">
            Join Our{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-200 to-teal-200">
              Volunteer Team
            </span>
          </h1>
          <p className="text-gray-200 text-lg max-w-2xl mx-auto">
            Be part of something bigger. Use your gifts to serve and make an eternal impact.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 space-y-8"
        >
          {/* Personal Information */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2 pb-4 border-b-2 border-emerald-200">
              <Users className="w-6 h-6 text-emerald-600" />
              Personal Information
            </h3>

            <div className="space-y-2" data-error={!!errors.full_name}>
              <label className="block text-sm font-semibold text-gray-700">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => handleInputChange("full_name", e.target.value)}
                className={`w-full px-4 py-3 rounded-xl border-2 ${errors.full_name ? "border-red-400" : "border-gray-200"} focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all`}
                placeholder="Enter your full name"
              />
              {errors.full_name && <p className="text-red-500 text-sm">{errors.full_name}</p>}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div data-error={!!errors.email}>
                <VolunteerEmailInput
                  value={formData.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("email", e.target.value)}
                  setFormData={setFormData}
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div data-error={!!errors.phone}>
                <VolunteerPhoneInput
                  value={formData.phone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("phone", e.target.value)}
                  setFormData={setFormData}
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>
            </div>

            <div className="space-y-2" data-error={!!errors.city_country}>
              <label className="block text-sm font-semibold text-gray-700">
                City / Country <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.city_country}
                  onChange={(e) => handleInputChange("city_country", e.target.value)}
                  className={`w-full pl-11 pr-4 py-3 rounded-xl border-2 ${errors.city_country ? "border-red-400" : "border-gray-200"} focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all`}
                  placeholder="e.g., Lagos, Nigeria"
                />
              </div>
              {errors.city_country && <p className="text-red-500 text-sm">{errors.city_country}</p>}
            </div>
          </div>

          {/* TSO Connection */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2 pb-4 border-b-2 border-emerald-200">
              <Handshake className="w-6 h-6 text-emerald-600" />
              Your TSO Connection
            </h3>

            <div className="space-y-2" data-error={!!errors.is_tso_member}>
              <label className="block text-sm font-semibold text-gray-700">
                Are you currently a member of TSO Ministries? <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4">
                {["Yes", "No"].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleInputChange("is_tso_member", option)}
                    className={`flex-1 py-3 rounded-xl font-medium transition-all border-2 ${
                      formData.is_tso_member === option
                        ? "bg-emerald-500 text-white border-emerald-600 shadow-lg"
                        : "bg-gray-50 text-gray-700 border-gray-200 hover:border-emerald-300"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
              {errors.is_tso_member && <p className="text-red-500 text-sm">{errors.is_tso_member}</p>}
            </div>

            <div className="space-y-2" data-error={!!errors.connection_duration}>
              <label className="block text-sm font-semibold text-gray-700">
                How long have you been connected to TSO Ministries? <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {["Less than 6 months", "6 months – 1 year", "1–3 years", "3+ years"].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleInputChange("connection_duration", option)}
                    className={`py-3 px-4 rounded-xl font-medium transition-all border-2 text-sm ${
                      formData.connection_duration === option
                        ? "bg-emerald-500 text-white border-emerald-600 shadow-lg"
                        : "bg-gray-50 text-gray-700 border-gray-200 hover:border-emerald-300"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
              {errors.connection_duration && <p className="text-red-500 text-sm">{errors.connection_duration}</p>}
            </div>

            <div className="space-y-2" data-error={!!errors.why_volunteer}>
              <label className="block text-sm font-semibold text-gray-700">
                Briefly share why you would like to volunteer with TSO Ministries{" "}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.why_volunteer}
                onChange={(e) => handleInputChange("why_volunteer", e.target.value)}
                rows={4}
                className={`w-full px-4 py-3 rounded-xl border-2 ${errors.why_volunteer ? "border-red-400" : "border-gray-200"} focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all resize-none`}
                placeholder="Share your heart and motivation..."
              />
              {errors.why_volunteer && <p className="text-red-500 text-sm">{errors.why_volunteer}</p>}
            </div>
          </div>

          {/* Department Interest */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2 pb-4 border-b-2 border-emerald-200">
              <Sparkles className="w-6 h-6 text-emerald-600" />
              Department Interest
            </h3>

            <div className="space-y-2" data-error={!!errors.department_interests}>
              <label className="block text-sm font-semibold text-gray-700">
                Which department(s) are you interested in serving in? (Select all that apply){" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {departments.map((dept) => (
                  <button
                    key={dept}
                    type="button"
                    onClick={() => toggleDepartment(dept)}
                    className={`py-3 px-4 rounded-xl font-medium transition-all border-2 text-left flex items-center gap-2 ${
                      formData.department_interests.includes(dept)
                        ? "bg-emerald-500 text-white border-emerald-600 shadow-lg"
                        : "bg-gray-50 text-gray-700 border-gray-200 hover:border-emerald-300"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        formData.department_interests.includes(dept) ? "bg-white border-white" : "border-gray-300"
                      }`}
                    >
                      {formData.department_interests.includes(dept) && (
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                      )}
                    </div>
                    <span className="text-sm">{dept}</span>
                  </button>
                ))}
              </div>
              {errors.department_interests && <p className="text-red-500 text-sm">{errors.department_interests}</p>}
            </div>

            <div className="space-y-2" data-error={!!errors.has_experience}>
              <label className="block text-sm font-semibold text-gray-700">
                Do you have previous experience in any of these areas? <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4">
                {["Yes", "No"].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleInputChange("has_experience", option)}
                    className={`flex-1 py-3 rounded-xl font-medium transition-all border-2 ${
                      formData.has_experience === option
                        ? "bg-emerald-500 text-white border-emerald-600 shadow-lg"
                        : "bg-gray-50 text-gray-700 border-gray-200 hover:border-emerald-300"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
              {errors.has_experience && <p className="text-red-500 text-sm">{errors.has_experience}</p>}
            </div>

            {formData.has_experience === "Yes" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="space-y-2"
              >
                <label className="block text-sm font-semibold text-gray-700">
                  Please briefly describe your experience or skills
                </label>
                <textarea
                  value={formData.experience_description}
                  onChange={(e) => handleInputChange("experience_description", e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all resize-none"
                  placeholder="Describe your relevant experience..."
                />
              </motion.div>
            )}

            <div className="space-y-2" data-error={!!errors.skills_talents}>
              <label className="block text-sm font-semibold text-gray-700">
                What skills, gifts, or talents do you believe God has given you that you can offer to TSO Ministries?{" "}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.skills_talents}
                onChange={(e) => handleInputChange("skills_talents", e.target.value)}
                rows={4}
                className={`w-full px-4 py-3 rounded-xl border-2 ${errors.skills_talents ? "border-red-400" : "border-gray-200"} focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all resize-none`}
                placeholder="Share your God-given gifts and talents..."
              />
              {errors.skills_talents && <p className="text-red-500 text-sm">{errors.skills_talents}</p>}
            </div>
          </div>

          {/* Commitment Questions */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2 pb-4 border-b-2 border-emerald-200">
              <Heart className="w-6 h-6 text-emerald-600" />
              Commitment & Availability
            </h3>

            {[
              {
                field: "open_to_different_dept",
                label: "Are you open to serving in a department different from your first choice if there is a need?",
              },
              {
                field: "willing_attend_training",
                label: "Are you willing to attend training, meetings, or orientation sessions if required?",
              },
              {
                field: "willing_serve_humbly",
                label: "Are you willing to serve with humility, accountability, and respect for ministry guidelines?",
              },
              {
                field: "willing_communicate_changes",
                label: "Do you agree to communicate openly if your availability or commitment changes?",
              },
            ].map(({ field, label }) => (
              <div key={field} className="space-y-2" data-error={!!errors[field]}>
                <label className="block text-sm font-semibold text-gray-700">
                  {label} <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  {["Yes", "No"].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => handleInputChange(field as keyof VolunteerFormData, option)}
                      className={`flex-1 py-3 rounded-xl font-medium transition-all border-2 ${
                        formData[field as keyof VolunteerFormData] === option
                          ? "bg-emerald-500 text-white border-emerald-600 shadow-lg"
                          : "bg-gray-50 text-gray-700 border-gray-200 hover:border-emerald-300"
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                {errors[field] && <p className="text-red-500 text-sm">{errors[field]}</p>}
              </div>
            ))}
          </div>

          {/* Final Section */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2 pb-4 border-b-2 border-emerald-200">
              <MessageCircle className="w-6 h-6 text-emerald-600" />
              Final Thoughts
            </h3>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Is there anything else you would like TSO leadership to know? (Optional)
              </label>
              <textarea
                value={formData.additional_info}
                onChange={(e) => handleInputChange("additional_info", e.target.value)}
                rows={4}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all resize-none"
                placeholder="Any additional information you'd like to share..."
              />
            </div>
          </div>

          {/* Submit Button */}
          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                  className="w-6 h-6 border-3 border-white border-t-transparent rounded-full"
                />
                Submitting...
              </>
            ) : (
              <>
                Submit Application
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </motion.button>
        </motion.form>
      </div>
    </div>
  )
}
